import moment from "moment";
import { useAuthContext } from "providers/Auth";
import { SectionCard } from "shared/app/SectionCard";
import { DateInput, Select } from "shared/components";
import { UnitRentField } from "./components/RentField";
import { PmsSyncOption } from "./components/PmsSyncOption";
import { PMSValue } from "./components/PmsValue";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";

const statusOptions = [
  { value: false, label: "Available" },
  { value: true, label: "Occupied" }
];

interface PMSRentalInfoFormProps {
  unitPmsId?: string;
  handleChange: any;
  values: any;
  register: any;
  headerExtra?: React.ReactNode;
  footer?: React.ReactNode;
}

export const PMSRentalInfoForm = ({
  unitPmsId,
  handleChange,
  values,
  register,
  headerExtra,
  footer
}: PMSRentalInfoFormProps) => {
  const { pms, type, permissions } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  return (
    <SectionCard
      title="Rental information"
      headerExtra={headerExtra}
      footer={footer}
    >
      <div className="space-y-8">
        <div className="space-y-8">
          {homeSiteRentEnabled ? (
            <>
              <UnitRentField
                handleChange={handleChange}
                values={values}
                register={register}
                rentType="home"
                id="homeRentAmount"
                label="Home rent"
                unitPmsId={unitPmsId}
              />
              <UnitRentField
                handleChange={handleChange}
                values={values}
                register={register}
                rentType="site"
                id="siteRentAmount"
                label="Site rent"
                unitPmsId={unitPmsId}
              />
            </>
          ) : (
            <UnitRentField
              handleChange={handleChange}
              values={values}
              register={register}
              rentType="monthly"
              id="monthlyRentAmount"
              label="Rent amount"
              unitPmsId={unitPmsId}
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <Tooltip>
              <TooltipTrigger className="w-full text-left">
                <Select
                  id="current-status"
                  label="Current status"
                  options={statusOptions}
                  required
                  {...register("occupied")}
                  isDisabled={
                    !permissions.canChangeAvailablebyDate ||
                    Boolean(unitPmsId && values.pms_occupied_override)
                  }
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                <p>
                  {values.pms_available_at_override
                    ? `Current status & available by date is synchronized with PMS.
                  You can manually edit these fields by disabling
                  synchronization for this field. To do this, click the 'Edit manually' option.`
                    : `Current status & available by date is currently edited manually.
                You can enable synchronization by clicking 'Sync with PMS' option`}
                </p>
              </TooltipContent>
            </Tooltip>
            <PmsSyncOption
              className="mt-4"
              name={register("pms_occupied_override").name}
              value={register("pms_occupied_override").value}
              onChange={(value) => {
                handleChange("pms_occupied_override")(value);
                handleChange("pms_available_at_override")(value);

                if (value) {
                  handleChange("occupied")(values.pms_occupied);
                  handleChange("available_at")(values.pms_available_at);
                }
              }}
            />
          </div>
          <PMSValue
            value={register("pms_occupied").value ? "Occupied" : "Available"}
          />
        </div>

        <div className="grid grid-cols-2 gap-4 items-center">
          <div>
            <Tooltip>
              <TooltipTrigger className="w-full text-left">
                <DateInput
                  id="available_at"
                  label="Available by date"
                  required
                  readOnly={
                    !values.occupied ||
                    !permissions.canChangeAvailablebyDate ||
                    Boolean(unitPmsId && values.pms_available_at_override)
                  }
                  {...register("available_at")}
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                <p>
                  {values.pms_available_at_override
                    ? `Current status & available by date is synchronized with PMS.
                  You can manually edit these fields by disabling
                  synchronization for this field. To do this, click the 'Edit manually' option.`
                    : `Current status & available by date is currently edited manually.
                You can enable synchronization by clicking 'Sync with PMS' option`}
                </p>
              </TooltipContent>
            </Tooltip>
            <PmsSyncOption
              name={register("pms_available_at_override").name}
              value={register("pms_available_at_override").value}
              onChange={(value) => {
                handleChange("pms_occupied_override")(value);
                handleChange("pms_available_at_override")(value);

                if (value) {
                  handleChange("occupied")(values.pms_occupied);
                  handleChange("available_at")(values.pms_available_at);
                }
              }}
            />
          </div>
          <PMSValue
            value={
              values["pms_available_at"]
                ? moment(values["pms_available_at"]).utc().format("MMM D, YYYY")
                : "N/A"
            }
          />
        </div>
      </div>
    </SectionCard>
  );
};
